import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Calendar, User } from 'lucide-react';
import { formatDate, calculateReadingTime } from '../../utils/blogUtils';
import { useTransition } from '../../hooks/useTransition';

const BlogCard = ({ post, compact = false }) => {
  const { navigateWithTransition, preloadRoute } = useTransition();
  
  if (!post) return null;

  // Parse tags if they're stored as JSON string
  const tags = typeof post.tags === 'string' ? JSON.parse(post.tags) : (post.tags || []);
  
  // Use created_at from database or publishedAt for compatibility
  const publishDate = post.created_at || post.publishedAt;

  // Handle card click with smooth transition
  const handleCardClick = (e) => {
    // Prevent navigation if clicking on interactive elements
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      return;
    }
    
    navigateWithTransition(`/blog/${post.slug}`);
  };

  // Handle hover for preloading
  const handleMouseEnter = () => {
    preloadRoute(`/blog/${post.slug}`);
  };

  // Compact version
  if (compact) {
    return (
      <article
        className="blog-card spotlight-card blog-card-compact"
        onClick={handleCardClick}
        onMouseEnter={handleMouseEnter}
        style={{ cursor: 'pointer' }}
      >
        <div className="spotlight-content">
          <div className="blog-card-compact-content">
            <div className="blog-card-compact-date">
              <Calendar size={14} />
              <span>{formatDate(publishDate)}</span>
            </div>
            <h2 className="blog-card-compact-title">
              {post.title}
            </h2>
          </div>
        </div>
      </article>
    );
  }

  // Original full version
  return (
    <article
      className="blog-card spotlight-card blog-card-wide"
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      style={{ cursor: 'pointer' }}
    >
      <div className="spotlight-content">
        <div className="blog-card-content">
          <div className="blog-card-main">
            <div className="blog-card-header">
              <h2 className="blog-card-title">
                {post.title}
              </h2>
              
              <div className="blog-card-meta">
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  <span>{formatDate(publishDate)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>{calculateReadingTime(post.content)}</span>
                </div>
              </div>
            </div>

            <div className="blog-card-excerpt">
              {post.excerpt}
            </div>
          </div>

          <div className="blog-card-sidebar">
            <div className="blog-card-tags">
              {tags.map((tag, index) => (
                <span key={index} className="blog-tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;